import api from '../api/client';
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    message,
    Modal,
    Form,
    Input,
    Select,
    InputNumber,
    Row,
    Col,
    Divider,
    Tag,
    Typography,
    Tabs,
    Popconfirm,
    Tooltip
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    SettingOutlined,
    SaveOutlined,
    CloseOutlined,
    ArrowUpOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import config from '../config';
import { Link } from 'react-router-dom';
import { apiFetch } from '../api';

const { Title, Text } = Typography;
const { Option } = Select;

// const apiFetch = async (path, opts = {}) => {
//     const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
//     const API = config[environment].apiUrl;
//     const r = await fetch(`${API}${path}`, { headers: { 'Content-Type': 'application/json' }, ...opts });
//     const d = await r.json();
//     if (!r.ok) throw new Error(d.message || 'Request failed');
//     return d;
// };

const AssetConfigPage = () => {
    const [types, setTypes] = useState([]);
    const [subtypes, setSubtypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('types');
    const [typeForm] = Form.useForm();
    const [subForm] = Form.useForm();
    const [saving, setSaving] = useState(false);

    // Edit modals state
    const [editTypeModalOpen, setEditTypeModalOpen] = useState(false);
    const [editSubModalOpen, setEditSubModalOpen] = useState(false);
    const [editType, setEditType] = useState(null);
    const [editSub, setEditSub] = useState(null);
    const [editTypeForm] = Form.useForm();
    const [editSubForm] = Form.useForm();

    const currentUser = useSelector((state) => state.account.user);

    // Load all data
    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [tr, sr] = await Promise.all([
                apiFetch('/api/animal-assets/config/types'),
                apiFetch('/api/animal-assets/config/subtypes')
            ]);
            setTypes(tr.data || []);
            setSubtypes(sr.data || []);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // ==================== ASSET TYPES ====================
    const saveType = async (values) => {
        setSaving(true);
        try {
            await apiFetch('/api/animal-assets/config/types', {
                method: 'POST',
                body: JSON.stringify(values)
            });
            message.success('Asset type added successfully');
            typeForm.resetFields();
            loadAll();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const updateType = async (values) => {
        setSaving(true);
        try {
            await apiFetch(`/api/animal-assets/config/types/${editType.id}`, {
                method: 'PUT',
                body: JSON.stringify(values)
            });
            message.success('Asset type updated successfully');
            setEditTypeModalOpen(false);
            setEditType(null);
            editTypeForm.resetFields();
            loadAll();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const deleteType = async (id, name) => {
        Modal.confirm({
            title: 'Delete Asset Type',
            content: `Are you sure you want to delete "${name}"? This may affect existing assets.`,
            okType: 'danger',
            onOk: async () => {
                try {
                    await apiFetch(`/api/animal-assets/config/types/${id}`, { method: 'DELETE' });
                    message.success('Asset type deleted');
                    loadAll();
                } catch (e) {
                    message.error(e.message);
                }
            }
        });
    };

    // ==================== SUBTYPES ====================
    const saveSubtype = async (values) => {
        setSaving(true);
        try {
            await apiFetch('/api/animal-assets/config/subtypes', {
                method: 'POST',
                body: JSON.stringify(values)
            });
            message.success('Subtype added successfully');
            subForm.resetFields();
            loadAll();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const updateSubtype = async (values) => {
        setSaving(true);
        try {
            await apiFetch(`/api/animal-assets/config/subtypes/${editSub.id}`, {
                method: 'PUT',
                body: JSON.stringify(values)
            });
            message.success('Subtype updated successfully');
            setEditSubModalOpen(false);
            setEditSub(null);
            editSubForm.resetFields();
            loadAll();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const deleteSubtype = async (id, name) => {
        Modal.confirm({
            title: 'Delete Subtype',
            content: `Delete "${name}"? Existing assets using this subtype will be unaffected.`,
            okType: 'danger',
            onOk: async () => {
                try {
                    await apiFetch(`/api/animal-assets/config/subtypes/${id}`, { method: 'DELETE' });
                    message.success('Subtype deleted');
                    loadAll();
                } catch (e) {
                    message.error(e.message);
                }
            }
        });
    };

    // Open edit modals
    const openEditTypeModal = (record) => {
        setEditType(record);
        editTypeForm.setFieldsValue({
            category: record.category,
            name: record.name,
            slug: record.slug,
            prefix: record.prefix,
            tag_format: record.tag_format,
            color: record.color,
            default_gl_debit: record.default_gl_debit,
            default_gl_credit: record.default_gl_credit,
            depreciation_method: record.depreciation_method,
            default_life_years: record.default_life_years,
            is_batch: record.is_batch,
            has_silo: record.has_silo,
            active: record.active
        });
        setEditTypeModalOpen(true);
    };

    const openEditSubModal = (record) => {
        setEditSub(record);
        editSubForm.setFieldsValue({
            type_id: record.type_id,
            name: record.name,
            code: record.code,
            active: record.active
        });
        setEditSubModalOpen(true);
    };

    // Table columns
    const typeColumns = [
        { title: 'Name', dataIndex: 'name', width: 120 },
        { title: 'Slug', dataIndex: 'slug', width: 100, render: (v) => <Text code>{v}</Text> },
        { title: 'Prefix', dataIndex: 'prefix', width: 70 },
        {
            title: 'Tag Format',
            dataIndex: 'tag_format',
            width: 140,
            render: (v) => (
                <Text code style={{ fontSize: 11 }}>
                    {v}
                </Text>
            )
        },
        { title: 'Category', dataIndex: 'category', width: 90, render: (v) => <Tag color={v === 'livestock' ? 'green' : 'blue'}>{v}</Tag> },
        { title: 'Batch', dataIndex: 'is_batch', width: 70, render: (v) => (v ? <Tag color="blue">Yes</Tag> : '—') },
        { title: 'Silo', dataIndex: 'has_silo', width: 70, render: (v) => (v ? <Tag color="green">Yes</Tag> : '—') },
        { title: 'GL Dr', dataIndex: 'default_gl_debit', width: 80 },
        { title: 'GL Cr', dataIndex: 'default_gl_credit', width: 80 },
        { title: 'Depreciation', dataIndex: 'depreciation_method', width: 120, render: (v) => v?.replace(/_/g, ' ') || 'None' },
        { title: 'Life (yrs)', dataIndex: 'default_life_years', width: 80, render: (v) => (v ? `${v}y` : '—') },
        { title: 'Color', dataIndex: 'color', width: 80, render: (v) => <Tag color={v}>{v || 'default'}</Tag> },
        {
            title: 'Status',
            dataIndex: 'active',
            width: 80,
            render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>)
        },
        {
            title: 'Actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit">
                        <Button size="small" icon={<EditOutlined />} onClick={() => openEditTypeModal(record)} />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteType(record.id, record.name)} />
                    </Tooltip>
                </Space>
            )
        }
    ];

    const subtypeColumns = [
        { title: 'Asset Type', dataIndex: 'type_name', width: 140 },
        { title: 'Subtype Name', dataIndex: 'name', width: 160 },
        { title: 'Code', dataIndex: 'code', width: 100, render: (v) => <Text code>{v || '—'}</Text> },
        {
            title: 'Status',
            dataIndex: 'active',
            width: 80,
            render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>)
        },
        {
            title: 'Actions',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit">
                        <Button size="small" icon={<EditOutlined />} onClick={() => openEditSubModal(record)} />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteSubtype(record.id, record.name)} />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <Card
            title={
                <Space>
                    <SettingOutlined />
                    <span>Asset Configuration</span>
                </Space>
            }
            extra={
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={loadAll}>
                        Refresh
                    </Button>
                    <Link to="/animal-assets">
                        <Button type="primary" icon={<ArrowLeftOutlined />}>
                            Back
                        </Button>
                    </Link>
                </Space>
            }
        >
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    {
                        key: 'types',
                        label: `Asset Types / Species (${types.length})`,
                        children: (
                            <>
                                <Table
                                    size="small"
                                    rowKey="id"
                                    loading={loading}
                                    pagination={{ pageSize: 15, showSizeChanger: true }}
                                    scroll={{ x: 1300 }}
                                    dataSource={types}
                                    columns={typeColumns}
                                    style={{ marginBottom: 24 }}
                                />

                                <Divider orientation="left">
                                    <Space>
                                        <PlusOutlined />
                                        Add New Asset Type
                                    </Space>
                                </Divider>

                                <Card size="small" style={{ background: '#fafafa' }}>
                                    <Form form={typeForm} layout="vertical" onFinish={saveType}>
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12} md={4}>
                                                <Form.Item
                                                    name="category"
                                                    label="Category"
                                                    rules={[{ required: true }]}
                                                    initialValue="livestock"
                                                >
                                                    <Select>
                                                        <Option value="livestock">Livestock</Option>
                                                        <Option value="physical">Physical</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={4}>
                                                <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                                                    <Input placeholder="e.g., Rabbit" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={3}>
                                                <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
                                                    <Input placeholder="rabbit" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={2}>
                                                <Form.Item name="prefix" label="Prefix" rules={[{ required: true }]}>
                                                    <Input placeholder="RB" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={4}>
                                                <Form.Item name="tag_format" label="Tag Format" initialValue="{PREFIX}-{SEQ5}">
                                                    <Input placeholder="{PREFIX}-{SEQ5}" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={2}>
                                                <Form.Item name="color" label="Color" initialValue="blue">
                                                    <Input placeholder="blue" />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Row gutter={16}>
                                            <Col xs={24} sm={12} md={3}>
                                                <Form.Item name="default_gl_debit" label="GL Debit">
                                                    <Input placeholder="1000" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={3}>
                                                <Form.Item name="default_gl_credit" label="GL Credit">
                                                    <Input placeholder="2000" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={4}>
                                                <Form.Item name="depreciation_method" label="Depreciation Method" initialValue="none">
                                                    <Select>
                                                        <Option value="none">None</Option>
                                                        <Option value="straight_line">Straight Line</Option>
                                                        <Option value="declining_balance">Declining Balance</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={2}>
                                                <Form.Item name="default_life_years" label="Life (years)">
                                                    <InputNumber min={0} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={2}>
                                                <Form.Item name="is_batch" label="Batch?" initialValue={0}>
                                                    <Select>
                                                        <Option value={0}>No</Option>
                                                        <Option value={1}>Yes</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={2}>
                                                <Form.Item name="has_silo" label="Silo?" initialValue={0}>
                                                    <Select>
                                                        <Option value={0}>No</Option>
                                                        <Option value={1}>Yes</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={2}>
                                                <Form.Item name="active" label="Active" initialValue={1}>
                                                    <Select>
                                                        <Option value={1}>Yes</Option>
                                                        <Option value={0}>No</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Form.Item>
                                            <Button type="primary" htmlType="submit" loading={saving} icon={<PlusOutlined />}>
                                                Add Asset Type
                                            </Button>
                                        </Form.Item>
                                    </Form>
                                </Card>
                            </>
                        )
                    },
                    {
                        key: 'subtypes',
                        label: `Subtypes / Breeds (${subtypes.length})`,
                        children: (
                            <>
                                <Table
                                    size="small"
                                    rowKey="id"
                                    loading={loading}
                                    pagination={{ pageSize: 15, showSizeChanger: true }}
                                    scroll={{ x: 600 }}
                                    dataSource={subtypes}
                                    columns={subtypeColumns}
                                    style={{ marginBottom: 24 }}
                                />

                                <Divider orientation="left">
                                    <Space>
                                        <PlusOutlined />
                                        Add New Subtype
                                    </Space>
                                </Divider>

                                <Card size="small" style={{ background: '#fafafa' }}>
                                    <Form form={subForm} layout="vertical" onFinish={saveSubtype}>
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12} md={6}>
                                                <Form.Item name="type_id" label="Asset Type" rules={[{ required: true }]}>
                                                    <Select placeholder="Select asset type" showSearch optionFilterProp="children">
                                                        {types.map((t) => (
                                                            <Option key={t.id} value={t.id}>
                                                                {t.name} ({t.slug})
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={6}>
                                                <Form.Item name="name" label="Subtype Name" rules={[{ required: true }]}>
                                                    <Input placeholder="e.g., Doe, Buck, Lamb" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={4}>
                                                <Form.Item name="code" label="Code">
                                                    <Input placeholder="DOE" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={3}>
                                                <Form.Item name="active" label="Active" initialValue={1}>
                                                    <Select>
                                                        <Option value={1}>Yes</Option>
                                                        <Option value={0}>No</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={5} style={{ display: 'flex', alignItems: 'flex-end' }}>
                                                <Form.Item>
                                                    <Button type="primary" htmlType="submit" loading={saving} icon={<PlusOutlined />} block>
                                                        Add Subtype
                                                    </Button>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card>
                            </>
                        )
                    }
                ]}
            />

            {/* Edit Type Modal */}
            <Modal
                title="Edit Asset Type"
                open={editTypeModalOpen}
                onCancel={() => {
                    setEditTypeModalOpen(false);
                    setEditType(null);
                    editTypeForm.resetFields();
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                {editType && (
                    <Form form={editTypeForm} layout="vertical" onFinish={updateType}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                                    <Select>
                                        <Option value="livestock">Livestock</Option>
                                        <Option value="physical">Physical</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="prefix" label="Prefix" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="tag_format" label="Tag Format">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="default_gl_debit" label="GL Debit">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="default_gl_credit" label="GL Credit">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="color" label="Color">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="depreciation_method" label="Depreciation">
                                    <Select>
                                        <Option value="none">None</Option>
                                        <Option value="straight_line">Straight Line</Option>
                                        <Option value="declining_balance">Declining</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={4}>
                                <Form.Item name="default_life_years" label="Life (years)">
                                    <InputNumber min={0} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="is_batch" label="Batch?">
                                    <Select>
                                        <Option value={0}>No</Option>
                                        <Option value={1}>Yes</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="has_silo" label="Silo?">
                                    <Select>
                                        <Option value={0}>No</Option>
                                        <Option value={1}>Yes</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="active" label="Active">
                                    <Select>
                                        <Option value={1}>Yes</Option>
                                        <Option value={0}>No</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <div style={{ textAlign: 'right', marginTop: 16 }}>
                            <Button onClick={() => setEditTypeModalOpen(false)} style={{ marginRight: 8 }}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={saving}>
                                Update
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal>

            {/* Edit Subtype Modal */}
            <Modal
                title="Edit Subtype"
                open={editSubModalOpen}
                onCancel={() => {
                    setEditSubModalOpen(false);
                    setEditSub(null);
                    editSubForm.resetFields();
                }}
                footer={null}
                width={500}
                destroyOnClose
            >
                {editSub && (
                    <Form form={editSubForm} layout="vertical" onFinish={updateSubtype}>
                        <Form.Item name="type_id" label="Asset Type" rules={[{ required: true }]}>
                            <Select showSearch>
                                {types.map((t) => (
                                    <Option key={t.id} value={t.id}>
                                        {t.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="name" label="Subtype Name" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="code" label="Code">
                            <Input />
                        </Form.Item>
                        <Form.Item name="active" label="Active">
                            <Select>
                                <Option value={1}>Yes</Option>
                                <Option value={0}>No</Option>
                            </Select>
                        </Form.Item>
                        <div style={{ textAlign: 'right', marginTop: 16 }}>
                            <Button onClick={() => setEditSubModalOpen(false)} style={{ marginRight: 8 }}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={saving}>
                                Update
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal>
        </Card>
    );
};

export default AssetConfigPage;
